import {
  DatePickerProviderProps,
  DatePickerStateProvider,
  DPDatesMode,
  useContextCalendars,
} from '@rehookify/datepicker'
import React, { useState } from 'react'

import Calendar from './calendar'

/**
 * Wrapper to provide the inner calendar component with context
 * @returns Calendar with context
 */
const CalendarWrapper: React.FC = () => {
  const { calendars } = useContextCalendars()

  return <Calendar calendar={calendars[0]} />
}

export interface DatePickerProps {
  /** The default dates to display */
  defaultDates?: Date[]
  /**
   * The mode of the date picker.
   *
   * 'single' - A single date can be selected
   *
   * 'range' - A range of dates can be selected
   *
   * 'multiple' - Multiple dates can be selected
   **/
  mode: DPDatesMode
  /** The minimum date that can be selected */
  minDate?: Date
  /** The maximum date that can be selected */
  maxDate?: Date
  /** The function to call when the dates are changed */
  onChange?: (dates: Date[]) => void
}

/**
 * A date picker. This is UI that can be used to select dates.
 * Can be placed inside of a modal or rendered on a page.
 *
 * Note: It does not have an input component.
 */
export const DatePicker = ({ defaultDates, mode, minDate, maxDate, onChange }: DatePickerProps) => {
  const [selectedDates, onDatesChange] = useState<Date[]>(defaultDates || [])

  const handleDatesChange = (dates: Date[]): void => {
    onDatesChange(dates)

    if (mode === 'range') {
      // Only output when the range is complete
      if (dates.length === 2) {
        onChange?.(dates)
      }
    } else {
      onChange?.(dates)
    }
  }

  // ReactNode cannot be returned because type could be string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component: (props: DatePickerProviderProps) => any = DatePickerStateProvider

  return (
    <>
      <Component
        config={{
          onDatesChange: handleDatesChange,
          selectedDates,
          dates: {
            mode,
            minDate,
            maxDate,
            selectSameDate: true,
          },
          years: { numberOfYears: 240, mode: 'fluid' },
          locale: {
            day: 'numeric',
            weekday: 'short',
            monthName: 'long',
            year: 'numeric',
          },
        }}
      >
        <CalendarWrapper />
      </Component>
    </>
  )
}

export default DatePicker
